// ===========================================
// Void Payment Dialog (#642 D4, D15)
// ===========================================
// Payments are VOIDED, never deleted: the row stays, struck through, with the reason. The same
// dialog is "Take off" for an account-credit row: the amount goes back to the family's credit
// and the invoice re-opens. A reason is required either way. The server's refusal is shown as
// it comes (CREDIT_IN_USE names the invoice to take the credit off first).

import { useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import type { Payment } from '../../api/invoices.api';
import { useVoidPayment } from '../../hooks/useInvoices';
import { useTakeOffCredit } from '../../hooks/useFamilyCredit';
import { apiErrorMessage } from '../../utils/apiError';
import { formatCurrencyFromDollars } from '../../utils/currency';

interface VoidPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  invoiceId: string;
  payment: Payment | null;
}

const MIN_REASON = 3;

export default function VoidPaymentDialog({ open, onClose, invoiceId, payment }: VoidPaymentDialogProps) {
  const [reason, setReason] = useState('');
  const voidMutation = useVoidPayment();
  const takeOffMutation = useTakeOffCredit();

  const isCredit = payment?.method === 'ACCOUNT_CREDIT';
  // The APPLIED movement an account-credit row was made by (it is what Take off voids).
  const appliedEntryId = payment?.FamilyCreditEntry?.find((e) => e.kind === 'APPLIED' && !e.voidedAt)?.id;
  const mutation = isCredit ? takeOffMutation : voidMutation;

  const close = () => {
    setReason('');
    voidMutation.reset();
    takeOffMutation.reset();
    onClose();
  };

  const confirm = async () => {
    if (!payment) return;
    try {
      if (isCredit) {
        if (!appliedEntryId) return;
        await takeOffMutation.mutateAsync({ entryId: appliedEntryId, reason: reason.trim() });
      } else {
        await voidMutation.mutateAsync({ paymentId: payment.id, invoiceId, reason: reason.trim() });
      }
      close();
    } catch {
      // Shown below, as the server worded it; stay open.
    }
  };

  const amount = payment ? formatCurrencyFromDollars(parseFloat(payment.amount)) : '';
  const valid = reason.trim().length >= MIN_REASON && (!isCredit || !!appliedEntryId);

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>{isCredit ? 'Take account credit off' : 'Void payment'}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {isCredit
            ? `${amount} goes back to the family's account credit and this invoice re-opens. It comes off again when their next invoice is sent.`
            : `The ${amount} payment stays on the invoice, struck through, with your reason. The invoice's balance goes back up.`}
        </Typography>
        <TextField
          fullWidth
          required
          autoFocus
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          inputProps={{ maxLength: 500 }}
          helperText={isCredit ? 'For example: to amend the invoice' : 'For example: recorded against the wrong invoice'}
        />
        {mutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {apiErrorMessage(mutation.error, isCredit ? 'Could not take the credit off' : 'Could not void the payment')}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={isCredit ? 'primary' : 'error'}
          onClick={confirm}
          disabled={!valid || mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isCredit ? 'Take off' : 'Void payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
